import { Component, inject, OnInit, OnDestroy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { APP_CONSTANTS } from '../../constants/app.constants';
import { MusicSyncService, SpotifyUserData, DiscogsUserData } from '../../services/music-sync.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  public musicSync = inject(MusicSyncService);

  // Constants for template
  readonly CONSTANTS = APP_CONSTANTS;

  // Debug panels visibility
  showSpotifyDebug = false;
  showDiscogsDebug = false;
  syncStartTime: number | null = null;
  syncTimer: any = null;
  syncElapsedTime = 0;

  public router = inject(Router);

  constructor() {
    // Reactive effects
    effect(() => {
      const progress = this.musicSync.connectionProgress();
      console.log(`🔄 Connection Progress: ${progress}%`);
    });

    effect(() => {
      const status = this.musicSync.connectionStatus();
      console.log(`📊 Connection Status: ${status}`);
    });

    // Expose service for debugging
    (window as any).musicSync = this.musicSync;
  }

  ngOnInit(): void {
    // Check for connection status from query params (after redirect from auth callback)
    this.route.queryParams.subscribe(params => {
      const connected = params['connected'];
      if (connected) {
        console.log(`✅ Successfully connected: ${connected}`);
        // Could show a success message or trigger a refresh
      }
    });
  }

  ngOnDestroy(): void {
    // Cleanup is handled by Angular signals automatically
  }

  // Connection Actions
  connectSpotify(): void {
    console.log('🎵 Dashboard: Connect Spotify button clicked');
    console.log('🎵 Dashboard: Spotify already connected?', this.musicSync.spotifyConnected());
    console.log('🎵 Dashboard: Current Spotify loading state:', this.musicSync.spotifyLoading());

    if (!this.musicSync.spotifyConnected()) {
      console.log('🎵 Dashboard: Calling musicSync.connectSpotify()');
      this.musicSync.connectSpotify();
    } else {
      console.log('🎵 Dashboard: Spotify already connected, skipping');
    }
  }

  async connectDiscogs(): Promise<void> {
    console.log('🎧 Dashboard: Connect Discogs button clicked');
    console.log('🎧 Dashboard: Discogs already connected?', this.musicSync.discogsConnected());
    console.log('🎧 Dashboard: Current Discogs loading state:', this.musicSync.discogsLoading());

    if (!this.musicSync.discogsConnected()) {
      console.log('🎧 Dashboard: Calling musicSync.connectDiscogs()');
      await this.musicSync.connectDiscogs();
    } else {
      console.log('🎧 Dashboard: Discogs already connected, skipping');
    }
  }

  disconnectSpotify(): void {
    this.musicSync.disconnectSpotify();
  }

  disconnectDiscogs(): void {
    this.musicSync.disconnectDiscogs();
  }

  // Refresh Data
  refreshSpotify(): void {
    this.musicSync.refreshSpotifyData();
  }

  refreshDiscogs(): void {
    this.musicSync.refreshDiscogsData();
  }

  // Debug Methods
  toggleSpotifyDebug(): void {
    this.showSpotifyDebug = !this.showSpotifyDebug;
  }

  toggleDiscogsDebug(): void {
    this.showDiscogsDebug = !this.showDiscogsDebug;
  }

  getSpotifyDebugData() {
    return {
      connected: this.musicSync.spotifyConnected(),
      loading: this.musicSync.spotifyLoading(),
      error: this.musicSync.spotifyError(),
      user: this.musicSync.spotifyUser(),
      librarySync: {
        loading: this.musicSync.spotifyLibrarySyncLoading(),
        data: this.musicSync.spotifyLibrarySyncData() ? 'Available (see Full Sync Response below)' : null,
        error: this.musicSync.spotifyLibrarySyncError()
      }
    };
  }

  getDiscogsDebugData() {
    return {
      connected: this.musicSync.discogsConnected(),
      loading: this.musicSync.discogsLoading(),
      error: this.musicSync.discogsError(),
      user: this.musicSync.discogsUser(),
      wishlist: this.musicSync.discogsWishlist().slice(0, 5), // Show first 5
      wishlistTotal: this.musicSync.discogsWishlist().length
    };
  }

    // Spotify Library Sync Debug Methods
  async syncSpotifyLibraryDebug(): Promise<void> {
    console.log('🎵 Dashboard: Sync Library (Debug Mode) button clicked');

    if (!this.musicSync.spotifyConnected()) {
      console.log('❌ Dashboard: Spotify not connected');
      return;
    }

    // Start timer for elapsed time tracking
    this.syncStartTime = Date.now();
    this.syncElapsedTime = 0;

    this.syncTimer = setInterval(() => {
      if (this.syncStartTime) {
        this.syncElapsedTime = Math.floor((Date.now() - this.syncStartTime) / 1000);
      }
    }, 1000);

    try {
      await this.musicSync.syncSpotifyLibraryDebug();
      console.log('✅ Dashboard: Library sync completed successfully');
    } catch (error: any) {
      // Only log as error if it's not a user cancellation
      if (error.message !== 'Sync cancelled by user') {
        console.error('❌ Dashboard: Library sync failed:', error);
      } else {
        console.log('🛑 Dashboard: Library sync cancelled by user');
      }
    } finally {
      // Stop timer
      if (this.syncTimer) {
        clearInterval(this.syncTimer);
        this.syncTimer = null;
      }
      setTimeout(() => {
        this.syncStartTime = null;
      }, 3000); // Clear timer after 3 seconds
    }
  }

  getSpotifyLibrarySyncStats() {
    const data = this.musicSync.spotifyLibrarySyncData();
    if (!data) return null;

    return {
      userName: data.user?.display_name || 'Unknown',
      userId: data.user?.id || 'Unknown',
      totalLikedTracks: data.stats.totalLikedTracks,
      totalSavedAlbums: data.stats.totalSavedAlbums,
      totalPlaylists: data.stats.totalPlaylists,
      totalUniqueArtists: data.stats.totalUniqueArtists,
      syncDurationMs: data.stats.syncDurationMs
    };
  }

  getSpotifyLibrarySyncFullData() {
    return this.musicSync.spotifyLibrarySyncData();
  }

  isDataTruncated(): boolean {
    const data = this.musicSync.spotifyLibrarySyncData();
    return data?._truncated === true;
  }

    getDataTruncationInfo() {
    const data = this.musicSync.spotifyLibrarySyncData();
    if (!data?._truncated) return null;

    return {
      originalCounts: data._originalCounts,
      showing: {
        likedTracks: data.likedTracks?.length || 0,
        savedAlbums: data.savedAlbums?.length || 0,
        playlists: data.playlists?.length || 0
      }
    };
  }

  isStatsOnlyMode(): boolean {
    const data = this.musicSync.spotifyLibrarySyncData();
    return data?._statsOnly === true;
  }

  getStatsOnlyMessage(): string {
    const data = this.musicSync.spotifyLibrarySyncData();
    return data?._message || 'Stats-only mode enabled for large libraries.';
  }

  cancelSpotifyLibrarySync(): void {
    console.log('🛑 Dashboard: Cancel sync button clicked');
    const cancelFn = this.musicSync.spotifyLibrarySyncCancel();
    if (cancelFn) {
      cancelFn();
    }
  }

  getPhaseIcon(phaseType: string): string {
    const phaseIcons: { [key: string]: string } = {
      'init': '🔄',
      'profile': '👤',
      'playlists': '📂',
      'liked_tracks': '❤️',
      'saved_albums': '💿',
      'playlist_tracks': '🎵',
      'analysis': '🔍',
      'artists': '🎤',
      'albums': '📀',
      'audio_features': '🎶',
      'processing': '⚙️',
      'complete': '✅',
      'error': '❌'
    };
    return phaseIcons[phaseType] || '🔄';
  }

  getPhaseList(): Array<{type: string, name: string, icon: string, completed: boolean, current: boolean}> {
    const currentPhaseType = this.musicSync.spotifyLibrarySyncPhaseType();
    const currentProgress = this.musicSync.spotifyLibrarySyncProgress();

    const phases = [
      { type: 'init', name: 'Initializing', progressRange: [0, 5] },
      { type: 'profile', name: 'User Profile', progressRange: [5, 10] },
      { type: 'playlists', name: 'Playlists', progressRange: [10, 20] },
      { type: 'liked_tracks', name: 'Liked Tracks', progressRange: [20, 50] },
      { type: 'saved_albums', name: 'Saved Albums', progressRange: [50, 65] },
      { type: 'playlist_tracks', name: 'Playlist Tracks', progressRange: [65, 75] },
      { type: 'analysis', name: 'Analysis', progressRange: [75, 80] },
      { type: 'artists', name: 'Artist Details', progressRange: [80, 85] },
      { type: 'albums', name: 'Album Details', progressRange: [85, 90] },
      { type: 'audio_features', name: 'Audio Features', progressRange: [90, 95] },
      { type: 'processing', name: 'Processing', progressRange: [95, 98] },
      { type: 'complete', name: 'Complete', progressRange: [98, 100] }
    ];

    return phases.map(phase => ({
      type: phase.type,
      name: phase.name,
      icon: this.getPhaseIcon(phase.type),
      completed: currentProgress > phase.progressRange[1],
      current: phase.type === currentPhaseType
    }));
  }

  // Utility Methods
  getSpotifyUserImage(): string | null {
    const user = this.musicSync.spotifyUser();
    return user?.images?.[0]?.url || null;
  }

  getDiscogsUserImage(): string | null {
    const user = this.musicSync.discogsUser();
    return user?.avatar_url || null;
  }

    formatConnectionTime(): string {
    const now = new Date();
    return now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
